import {useCallback} from 'react';
import {useSelector} from 'react-redux';

import {Flyout, FormGenerator} from '@macmillanplatform/ui-core';
import {useVisibility} from '@macmillanplatform/ui-core/hooks';

import {CONFIGS_FIELDS, PRODUCT_INFO} from '@/constants';

import {clearAuthData, saveCredentials} from '@/features/auth/helpers';
import {useAppDispatch} from '@/hooks';

import {fetchOrders} from '@/actions';
import {fetchUser} from '@/features/auth/slice';
import {selectConfig, selectConfigField} from '@/reducer';

import AuthFooter from './components/auth-footer';
import ContactUs from './components/contact-us';
import {LoginPage as LoginPageComponent} from '@/shared/components';

import './index.css';

const LoginPage = () => {
  const dispatch = useAppDispatch();
  const config = useSelector(selectConfig);
  const authFields = useSelector(
    selectConfigField(CONFIGS_FIELDS.AUTHORIZATION_FIELDS)
  );

  const {isVisible, toggle} = useVisibility();

  interface AuthFormValues {
    link_account: string;
    zip_code: string;
  }

  const handleSubmit = useCallback(async (values: AuthFormValues) => {
    try {
      const {link_account, zip_code} = values;

      saveCredentials(link_account, zip_code);

      const response = await dispatch(
        fetchOrders({urlParams: new URLSearchParams()})
      ).unwrap();

      const ordersCount = response?.count ?? 0;

      if (ordersCount > 0) {
        await dispatch(fetchUser());
      } else {
        clearAuthData();
        new Flyout({
          type: 'error',
          children:
            'No orders were found for the account number and zip code combination you entered. Please double-check your account or bill to number and zip code and try again. If you are a Canadian or international customer, please remember to enter only the first 5 characters of your zip code, including any spaces.',
          timeout: 30000,
        });
      }
    } catch (error) {
      new Flyout({
        type: 'error',
        children:
          'An error occurred while submitting the form. Please try again.',
        timeout: 5000,
      });
    }
  }, []);

  return (
    <>
      <main className="Product__main Login">
        <LoginPageComponent
          {...PRODUCT_INFO}
          customAuth={
            <FormGenerator
              shouldUseRhf
              config={config}
              fields={authFields}
              onSubmit={handleSubmit}
              className="AuthForm__form-generator"
            >
              {({
                form: {
                  formState: {errors, isValid, isSubmitting},
                },
                generateFormFields,
              }) => (
                <div className="AuthForm__container">
                  <section className="AuthForm__content">
                    {generateFormFields()}
                  </section>
                  <AuthFooter
                    isValid={isValid}
                    isSubmitting={isSubmitting}
                    errors={errors}
                    toggle={toggle}
                  />
                </div>
              )}
            </FormGenerator>
          }
        />
      </main>
      <ContactUs show={isVisible} toggle={toggle} />
    </>
  );
};

export default LoginPage;
